import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { toast } from "react-toastify";
import { PhoneNumberUtil } from "google-libphonenumber";
import moment from "moment";

import { CRMService, LeadService } from "../../services";
import { leadValidationRules } from "../../pages/crm-pro/validation";
import { ActionButton, FormDialog, InputField, PhoneInput, SelectField } from "../v3";

const phoneUtil = PhoneNumberUtil.getInstance();

const isPhoneValid = (phone) => {
  try {
    return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone));
  } catch {
    return false;
  }
};

const SOURCE_OPTIONS = ["Website", "Referral", "Walk-in", "Social Media", "Advertisement", "Phone Call", "Other"];

const getEmptyLeadDetails = () => ({
  contactName: "",
  contactPhone: "",
  contactEmail: "",
  priorityId: undefined,
  priority: undefined,
  source: "",
  expectedBudget: "",
  expectedStartDate: moment().format("YYYY-MM-DD"),
  siteAddress: { locality: "", city: "", state: "" },
  date: moment().format("YYYY-MM-DD"),
});

export const AddEditLead = ({ open, onClose, leadId, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<any>({});
  const [touched, setTouched] = useState(false);
  const [leadDetails, setLeadDetails] = useState<any>(getEmptyLeadDetails);
  const [selectedPriority, setSelectedPriority] = useState<any>(null);
  const [sourceOption, setSourceOption] = useState("");

  useEffect(() => {
    if (open && leadId) {
      fetchLeadDetails(leadId);
    } else if (open && !leadId) {
      handleClearForm();
    }
  }, [open, leadId]);

  const deriveSourceOption = (source) => {
    if (!source) return "";
    return SOURCE_OPTIONS.includes(source) && source !== "Other" ? source : "Other";
  };

  const fetchLeadDetails = async (id: number) => {
    try {
      const lead = await LeadService.getLeadById(id);
      setLeadDetails({
        contactName: lead?.contactName || "",
        contactPhone: lead?.contactPhone || "",
        contactEmail: lead?.contactEmail || "",
        source: lead?.source || "",
        expectedBudget: lead?.expectedBudget ?? "",
        expectedStartDate: lead?.expectedStartDate ? moment(lead.expectedStartDate).format("YYYY-MM-DD") : "",
        siteAddress: {
          locality: lead?.siteAddress?.locality || "",
          city: lead?.siteAddress?.city || "",
          state: lead?.siteAddress?.state || "",
        },
        date: lead?.date ? moment(lead.date).format("YYYY-MM-DD") : undefined,
      });
      setSourceOption(deriveSourceOption(lead?.source));
    } catch (error) {
      toast("Failed to fetch lead details", { type: "error" });
      console.error("fetchData error:", error);
    }
  };

  const handlePriorityChange = (selected?: any) => {
    setSelectedPriority(selected);
    setLeadDetails((prev) => ({ ...prev, priorityId: selected?.id || null, priority: selected || null }));
  };

  const handleClearForm = () => {
    setLeadDetails(getEmptyLeadDetails());
    setSelectedPriority(null);
    setSourceOption("");
    setTouched(false);
  };

  // Params are optional here purely to satisfy InputField's inferred `onChange = () => {}`
  // default (a TS-only artifact — MUI always calls these with the real event at runtime).
  const updateDetailsState = (event?: any) => {
    const { name, value } = event.target;
    setLeadDetails((prev) => ({ ...prev, [name]: value }));
  };

  const updateSiteAddressState = (event?: any) => {
    const { name, value } = event.target;
    setLeadDetails((prev) => ({ ...prev, siteAddress: { ...prev.siteAddress, [name]: value } }));
  };

  const handlePhoneChange = (e164?: string) => {
    setLeadDetails((prev) => ({ ...prev, contactPhone: e164 }));
  };

  const handleSourceOptionChange = (event?: any) => {
    const value = event.target.value;
    setSourceOption(value);
    setLeadDetails((prev) => ({ ...prev, source: value === "Other" ? "" : value }));
  };

  const handleCloseModal = () => {
    handleClearForm();
    setErrors({});
    onClose();
  };

  const isPhoneInvalid = !!leadDetails.contactPhone && !isPhoneValid(leadDetails.contactPhone);
  const showPhoneError = (touched && (!leadDetails.contactPhone || isPhoneInvalid)) || !!errors.contactPhone;

  const handleSaveLead = async () => {
    setErrors({});
    setTouched(true);

    const validationErrors: any = leadValidationRules(leadDetails, leadId);
    if (leadDetails.contactPhone && isPhoneInvalid) {
      validationErrors.contactPhone = "Enter a valid phone number";
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const basePayload = {
        name: leadDetails.contactName,
        contactName: leadDetails.contactName,
        contactPhone: leadDetails.contactPhone,
        contactEmail: leadDetails.contactEmail || undefined,
        source: leadDetails.source || undefined,
        expectedBudget: leadDetails.expectedBudget === "" ? undefined : Number(leadDetails.expectedBudget),
        expectedStartDate: leadDetails.expectedStartDate || undefined,
        siteAddress:
          leadDetails.siteAddress?.locality || leadDetails.siteAddress?.city || leadDetails.siteAddress?.state
            ? leadDetails.siteAddress
            : undefined,
        date: leadDetails.date,
      };

      if (leadId) {
        await LeadService.updateLead(leadId, basePayload);
      } else {
        await LeadService.createLead({ ...basePayload, priorityId: leadDetails.priorityId });
      }

      toast.success(`Lead ${leadId ? "updated" : "added"} successfully`);

      if (onSuccess) onSuccess();
      handleCloseModal();
    } catch (error) {
      let errorMessage = `Error ${leadId ? "updating" : "adding"} lead`;
      const response = error?.response?.data;

      if (Array.isArray(response?.message)) {
        errorMessage = response.message.join(", ");
      } else if (typeof response?.message === "string") {
        errorMessage = response.message;
      }

      toast(errorMessage, { type: "error" });
      console.error("handleSaveLead error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={handleCloseModal}
      onSubmit={handleSaveLead}
      title={leadId ? "Edit Lead" : "Add Lead"}
      actions={
        <>
          <ActionButton buttonText="Cancel" onButtonClick={handleCloseModal} variant="outlined" />
          <ActionButton buttonText="Save" onButtonClick={handleSaveLead} loading={loading} />
        </>
      }
    >
      <Box mt={1}>
        <Box className="fl gap-2">
          <InputField
            required
            label="Contact Name"
            name="contactName"
            value={leadDetails.contactName}
            onChange={updateDetailsState}
            error={!!errors.contactName}
            helperText={errors.contactName}
            style={{ width: "100%" }}
          />
          <PhoneInput
            required
            label="Mobile"
            name="contactPhone"
            value={leadDetails.contactPhone}
            onChange={handlePhoneChange}
            onBlur={() => setTouched(true)}
            error={showPhoneError}
            helperText={
              errors.contactPhone ||
              (showPhoneError ? (leadDetails.contactPhone ? "Enter a valid phone number" : "Mobile number is required") : " ")
            }
            fullWidth
            style={{ width: "100%" }}
          />
        </Box>
        <Box className="fl gap-2">
          <InputField
            label="Email"
            name="contactEmail"
            value={leadDetails.contactEmail}
            onChange={updateDetailsState}
            error={!!errors.contactEmail}
            helperText={errors.contactEmail}
            style={{ width: "100%" }}
          />
          <InputField
            label="Source"
            name="sourceOption"
            select
            selectOptions={SOURCE_OPTIONS.map((option) => ({ label: option, value: option }))}
            value={sourceOption}
            onChange={handleSourceOptionChange}
            style={{ width: "100%" }}
          />
        </Box>
        {sourceOption === "Other" && (
          <Box className="fl gap-2">
            <InputField
              label="Please specify source"
              name="source"
              value={leadDetails.source}
              onChange={updateDetailsState}
              style={{ width: "100%" }}
            />
          </Box>
        )}
        <Box className="fl gap-2">
          {!leadId && (
            <SelectField
              label="Priority"
              value={selectedPriority}
              getData={CRMService.getLeadPriorities}
              getDataStaticFilters={{ includeGlobal: true }}
              required
              error={!!errors.priorityId}
              helperText={errors.priorityId}
              onChange={handlePriorityChange}
              style={{ width: "100%" }}
            />
          )}
          <InputField
            label="Date"
            name="date"
            type="date"
            value={leadDetails.date}
            onChange={updateDetailsState}
            error={!!errors.date}
            helperText={errors.date}
            style={{ width: "100%" }}
          />
        </Box>
        <Box className="fl gap-2">
          <InputField
            label="Expected Budget"
            name="expectedBudget"
            type="number"
            value={leadDetails.expectedBudget}
            onChange={updateDetailsState}
            error={!!errors.expectedBudget}
            helperText={errors.expectedBudget}
            inputSlotProps={{ htmlInput: { min: 0 } }}
            style={{ width: "100%" }}
          />
          <InputField
            label="Expected Start Date"
            name="expectedStartDate"
            type="date"
            value={leadDetails.expectedStartDate}
            onChange={updateDetailsState}
            style={{ width: "100%" }}
          />
        </Box>
        <Box className="fl gap-2">
          <InputField
            label="Site Locality"
            name="locality"
            value={leadDetails.siteAddress.locality}
            onChange={updateSiteAddressState}
            style={{ width: "100%" }}
          />
          <InputField
            label="Site City"
            name="city"
            value={leadDetails.siteAddress.city}
            onChange={updateSiteAddressState}
            style={{ width: "100%" }}
          />
          <InputField
            label="Site State"
            name="state"
            value={leadDetails.siteAddress.state}
            onChange={updateSiteAddressState}
            style={{ width: "100%" }}
          />
        </Box>
      </Box>
    </FormDialog>
  );
};
